/**
 * Production Environment Configuration
 *
 * This configuration is optimized for production deployment with:
 * - Secure HTTPS endpoints
 * - Error-only logging
 * - Performance optimizations
 * - Security hardening
 * - Real trading capabilities
 */
import type { Environment } from './environment.interface';

export const environment: Environment = {
  // Environment identification
  production: true,
  staging: false,
  development: false,
  version: '7e146eb74',
  buildTime: new Date().toISOString(),

  // API Configuration (same-domain path-based routing)
  apiUrl: 'https://razorsedge.io/api/v1',
  apiVersion: 'v1',
  apiTimeout: 10000, // 10 seconds timeout

  // WebSocket Configuration for HFT Real-time Data (same-domain)
  websocketUrl: 'wss://razorsedge.io/ws',
  wsUrl: 'wss://razorsedge.io/ws',
  wsReconnectInterval: 1000, // Aggressive reconnection
  wsMaxReconnectAttempts: 50,
  wsHeartbeatInterval: 15000, // More frequent heartbeats

  // Specialized WebSocket endpoints for HFT (same-domain)
  marketDataWsUrl: 'wss://razorsedge.io/ws/market-data',
  tradingWsUrl: 'wss://razorsedge.io/ws/trading',
  adminWsUrl: 'wss://razorsedge.io/ws/admin',
  riskManagementWsUrl: 'wss://razorsedge.io/ws/risk',
  orderBookWsUrl: 'wss://razorsedge.io/ws/order-book',
  symbolMappingWsUrl: 'wss://razorsedge.io/ws/symbols',
  systemHealthWsUrl: 'wss://razorsedge.io/ws/system-health',

  // Feature Flags
  features: {
    // Development features (disabled)
    enableDevTools: false,
    enableMockData: false,
    enableDashboardMock: false, // Dashboard uses real API in production
    enableSystemHealthMock: false, // System Health uses real API in production
    enableDebugPanel: false,
    enableSourceMaps: false,
    enableHotReload: false,
    enablePerformanceDebugging: false,
    
    // Core features
    enableWebSockets: true,
    enableNotifications: true,
    enableDarkMode: true,
    enableAdvancedAnalytics: true,
    enableAutoTrading: true, // Enabled for production
    enableRealTimeData: true,
    enableOrderManagement: true,
    enableRiskManagement: true,
    enableSymbolMapping: true,
    enableMultiTenant: true,
    enableMLAnomalyDetection: true,
    enableAnomalyDetectionMock: false,
    enableTenantMock: false,
    enableSymbolMappingMock: false,
    enableSchemaMock: false,
    enablePredictiveMaintenance: true,
    // GAP-FE-117 / GAP-050: the copilot + agent backends are implemented
    // and mounted (routes/copilot.rs, routes/agent.rs). These flags are
    // now pure FE rollout switches — kept OFF in prod pending the
    // operator decision to surface the agent/copilot UX to end users.
    enableCopilotSummaries: false,
    // Interactive AG-UI copilot chat — build-time capability SHIPPED (true) so
    // enabling needs no FE rebuild. The RUNTIME gate keeps it dark: the panel +
    // palette entry only appear when GET /api/v1/copilot/status returns
    // enabled:true (backend defaults disabled until COPILOT_CHAT_ENABLED is set
    // and the razor-copilot sidecar is deployed). This flag stays the HARD
    // kill-switch — set false to force-remove the feature from the build.
    enableCopilotChat: true,
    enableAgentProposals: false,
    // GAP-FE-H003: agent-controls/agent-invariants ride the same rollout
    // switch decision as enableAgentProposals (backend live per GAP-050).
    enableAgentControls: false,
    enableAgentInvariants: false,
    // GAP-024: Monitoring Configuration page is wired to the real
    // GET/PUT /api/v1/system-health/configuration endpoints. Enabled.
    enableMonitoringConfig: true,
    // GAP-009: self-service payment-method entry + deposit/withdrawal
    // GAP-045: payment-policy endpoint ships, but the disclosure panel stays
    // OFF in prod until the operator sets the real PAYMENT_POLICY_* env values
    // (see docs/ops/open-gaps-operator-runbook.md) — otherwise the backend's
    // built-in DEFAULT fee figures would render as authoritative client-facing
    // financial disclosures. Flip to true only after configuring the env.
    enablePaymentPolicyDisclosure: false,
    // GAP-010: no companion mobile app and no backend mobilePush field exist,
    // so the Settings "Mobile Notifications" toggle is hidden. Keep false until
    // both ship (the value would otherwise persist to localStorage only and
    // deliver nothing).
    enableMobilePushNotifications: false,

    // UI features
    enableVirtualScrolling: true,
    enableTableFiltering: true,
    enableDataExport: true,
    enableUserPreferences: true,

    // Localization features
    // GAP-FE-U001: disabled in non-local environments — the translate pipe is
    // wired in 0 templates today. See `shared/services/AUDIT_FE_DECISIONS.md`
    // §GAP-FE-C015 for the deferral rationale; flip back to true once a
    // library decision is made and templates are swept.
    multiLanguageSupport: false
  },

  // Authentication & Security
  auth: {
    tokenExpirationMinutes: 30, // Shorter for production
    refreshTokenExpirationDays: 1, // Shorter for security
    enableRememberMe: false, // Disabled for security
    sessionTimeout: 1800000, // 30 minutes
    tokenRefreshInterval: 900000, // 15 minutes
    maxLoginAttempts: 3, // Stricter
    passwordMinLength: 12,
    requireMFA: true, // Enabled for production
    enableSSOLogin: true
  },

  // UI Configuration
  ui: {
    theme: 'light',
    language: 'en',
    enableNotifications: true,
    notificationDuration: 3000, // Shorter
    defaultPageSize: 50, // More data per page
    enableAnimations: false, // Disabled for performance
    enableRippleEffect: false, // Disabled for performance
    showPerformanceMetrics: false,
    enableKeyboardShortcuts: true,
    autoRefreshInterval: 1000 // 1 second for real-time
  },

  // Logging Configuration
  logging: {
    level: 'error', // Error-only logging
    enableConsoleLog: false,
    enableRemoteLog: true,
    enableFileLog: false,
    enablePerformanceLog: false,
    logToStorage: false,
    maxLogEntries: 100, // Limited storage
    logApiCalls: false,
    logWebSocketEvents: false,
    logStateChanges: false
  },

  // HFT Trading Configuration
  trading: {
    maxOrderSize: 1000000, // Production limits
    minOrderSize: 0.001,
    defaultSlippage: 0.5, // Tight slippage
    orderTimeoutMs: 5000, // Fast timeout
    priceUpdateIntervalMs: 100, // 100ms updates for HFT
    enablePaperTrading: false,
    enableOrderSimulation: false,
    maxPositionSize: 5000000,
    riskLimitCheckInterval: 100 // Very frequent risk checks
  },

  // Market Data Configuration
  marketData: {
    updateIntervalMs: 100, // Real-time updates
    historyDepthDays: 365, // More history
    enableL2Data: true,
    enableTickData: true,
    enableCandlestickData: true,
    maxSymbolsPerConnection: 1000,
    enableDataCompression: true // Enabled for bandwidth
  },

  // Performance Monitoring
  monitoring: {
    enableErrorReporting: true,
    enablePerformanceTracking: true,
    enableMemoryTracking: false, // Disabled for performance
    sampleRate: 0.1, // 10% sampling
    errorReportingUrl: null, // Error reporting handled by Sentry (see sentry config below)
    performanceThresholds: {
      apiResponseTime: 500, // Stricter
      wsLatency: 50, // Very strict for HFT
      renderTime: 16.67 // 60 FPS
    }
  },

  // Sentry Error Monitoring
  sentry: {
    enabled: true,
    dsn: 'https://cbff35b50ec3e9e44c87f0a09170727b@o4510795582144512.ingest.de.sentry.io/4510795758633040',
    tracesSampleRate: 0.1, // 10% of transactions for performance monitoring
    sampleRate: 0.25, // 25% of error events — throttled to avoid ingest 429 under bursts
    replaysSessionSampleRate: 0, // Disabled for privacy/performance
    replaysOnErrorSampleRate: 0, // Disabled for privacy/performance
  },

  // Exchange Configurations (Production)
  exchanges: {
    binance: {
      enabled: true,
      apiUrl: 'https://api.binance.com',
      wsUrl: 'wss://stream.binance.com:9443/ws',
      sandbox: false,
      rateLimitPerSecond: 100
    },
    coinbase: {
      enabled: true,
      apiUrl: 'https://api.exchange.coinbase.com',
      wsUrl: 'wss://ws-feed.exchange.coinbase.com',
      sandbox: false,
      rateLimitPerSecond: 50
    },
    kraken: {
      enabled: true,
      apiUrl: 'https://api.kraken.com',
      wsUrl: 'wss://ws.kraken.com',
      sandbox: false,
      rateLimitPerSecond: 20
    },
    bitfinex: {
      enabled: false,
      apiUrl: 'https://api.bitfinex.com',
      wsUrl: 'wss://api-pub.bitfinex.com/ws/2',
      sandbox: false,
      rateLimitPerSecond: 10
    }
  },

  // Rate Limiting (Production)
  rateLimits: {
    apiCallsPerSecond: 1000, // High throughput
    wsMessagesPerSecond: 10000, // Very high for HFT
    burstAllowance: 500,
    enableRateLimiting: true,
    rateLimitWarningThreshold: 0.9
  },

  // Database Configuration
  database: {
    enableQueryLogging: false,
    connectionTimeout: 5000, // Faster timeout
    maxConnections: 100, // More connections
    enableMigrations: false // Manual in production
  },

  // Cache Configuration
  cache: {
    enableCaching: true,
    defaultTTL: 60000, // 1 minute
    maxCacheSize: 1000, // 1GB
    enablePersistentCache: true,
    userCacheTTL: 3 * 60 * 1000, // 3 minutes for user data in production
    listCacheTTL: 1 * 60 * 1000, // 1 minute for user lists
  },

  // Bulk Operations Configuration
  // Rate limiting for batch processing to prevent server overload
  bulkOperations: {
    batchSize: 5, // Number of items to process per batch
    batchDelayMs: 200, // Delay between batches in milliseconds
    maxConcurrentOperations: 10, // Maximum concurrent operations
    timeoutMs: 30000, // Timeout for each bulk operation
  },

  // Security Configuration
  security: {
    enableCSP: true,
    enableCORS: true,
    allowedOrigins: ['https://razorsedge.io'],
    enableXSSProtection: true,
    enableClickjacking: true,
    enableHSTS: true, // HTTPS strict transport
    contentSecurityPolicy: {
      defaultSrc: ["'self'"],
      scriptSrc: ["'self'"],
      styleSrc: ["'self'", "'unsafe-inline'"], // Razor UI components use inline styles
      imgSrc: ["'self'", 'data:'],
      connectSrc: [
        "'self'",
        'https://razorsedge.io',
        'wss://razorsedge.io',
        'https://api.binance.com',
        'wss://stream.binance.com:9443',
        'https://api.exchange.coinbase.com',
        'wss://ws-feed.exchange.coinbase.com',
        'https://api.kraken.com',
        'wss://ws.kraken.com',
        'https://api.bitfinex.com',
        'wss://api-pub.bitfinex.com',
        'https://*.sentry.io',
        'https://*.ingest.sentry.io',
        'https://*.ingest.de.sentry.io'
      ]
    }
  },

  // Development Tools (all disabled)
  devTools: {
    enableReduxDevTools: false,
    enableAngularDevTools: false,
    enableNetworkInspection: false,
    enableWebSocketInspection: false,
    enableStateInspection: false
  },

  // Platform-owned URLs — GAP-FE-028. Single domain (CLAUDE.md). No subdomains.
  urls: {
    support: 'https://razorsedge.io/support',
    supportEmail: 'support@razorsedge.io',
    restApiBase: 'https://razorsedge.io/api/v1',
    wsBase: 'wss://razorsedge.io/ws',
    primaryDomain: 'razorsedge.io',
    legalEmail: 'legal@razorsedge.io',
  },

  // Third-party / external CDN URLs — GAP-FE-028.
  externalUrls: {
    cryptoIconCdn: 'https://assets.coincap.io/assets/icons',
  }
};
